import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiError, RequestParameters } from '../types/apiTypes';
import { OrgLocation } from '../types/types';
import { getValidUserFromLocalStorage, isUserExpired } from '../utilities/user';

axios.defaults.baseURL = config.ApiUrl;
axios.defaults.timeout = 600000;

class Api {
    static readonly apiClient: AxiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_HOST,
        responseType: 'json',
        timeout: 600000,
        headers: {
            'Content-Type': 'application/json',
        },
    });
    static async doGetRequestAsync<T>(url: string) {
        try {
            const response = await axios.get<T>(url);
            return response.data;
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doGetRequestAsync', url, err);
            }
            throw err;
        }
    }
    static setHeaders() {
        const user = getValidUserFromLocalStorage();
        if (user === null) return;
        axios.defaults.headers.common['Authorization'] = `Bearer ${user.access_token}`;
    }

    static async doAuthGetRequestAsync<T>(url: string) {
        try {
            this.setHeaders();
            const response = await axios.get<T>(url);
            return response.data;
        } catch(err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthGetRequestAsync', url, err);
            }
            throw err;
        }
    }

    static async doAuthGetRequestAsyncWithParams<T>(url: string, parameters?: RequestParameters) {
        try {
            this.setHeaders();
            const config = Api.getConfig(parameters);
            const response = await axios.get<T>(url, config);
            return response.data;
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthGetRequestAsyncWithParams', url, err);
            }
            throw err;
        }
    }

    static async doAuthGetRequestLongOperation<T>(url: string, retryDelayMs = 5000, maxPools = 100): Promise<T> {
        try {
            let poolsCount = 0;
            while (poolsCount < maxPools) {
                this.setHeaders();
                const response = await axios.get<T>(url);
                if (response.status === 202) {
                    await new Promise(res => setTimeout(res, retryDelayMs));
                    poolsCount++;
                } else {
                    return response.data;
                }
            }
            throw new Error('Maximum retries exceeded for long operation');
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthGetRequestLongOperation', url, err);
            }
            throw err;
        }
    }

    static async doAuthNotificationApiGetRequestAsync(url: string) {
        try {
            this.setHeaders();
            const response = await axios({ url, baseURL: config.NotificationsApiUrl });
            return response.data;
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthNotificationApiGetRequestAsync', url, err);
            }
            throw err;
        }
    }

    static async doAuthGetFileRequestAsync(url: string) {
        try {
            this.setHeaders();
            const response = await axios({
                url,
                method: 'GET',
                responseType: 'blob', // important
            });
            return response.data;
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthGetFileRequestAsync', url, err);
            }
            throw err;
        }
    }

    static async doAuthPostRequestAsync<T, R>(url: string, data?: T, parameters?: RequestParameters) {

        try {
            this.setHeaders();
            const config = Api.getConfig(parameters);
            const response = await axios.post<R>(url, data, config);
            return response.data;
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthPostRequestAsync', url, err);
            }
            throw err;
        }
    }
    static async doAuthPatchRequestAsync<T, R>(url: string, data?: T, parameters?: RequestParameters) {
        try {
            this.setHeaders();
            const config = Api.getConfig(parameters);
            const response = await axios.patch<R>(url, data, config);
            return response.data;
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthPatchRequestAsync', url, err);
            }
            throw err;
        }
    }
    static async doAuthDeleteRequestAsync<R>(url: string) {
        try {
            this.setHeaders();
            const response = await axios.delete<R>(url);
            return response.data;
        } catch (err) {
            if (Api.isApiError(err)) {
                Api.catchError('doAuthDeleteRequestAsync', url, err);
            }
            throw err;
        }
    }
    static async getOrgLocation(agencyId: string) {
        return await this.doAuthGetRequestAsync<OrgLocation>('/Service.svc/GetOrgLocation?orgId=' + agencyId);
    }
    static async refreshDataCache(forceNotExpired: boolean) {
        return await this.doAuthGetRequestAsync<string>('/Service.svc/RefreshGTFS?forceNotExpired=' + forceNotExpired);
    }

    static getConfig(parameters?: RequestParameters) {
        const config: AxiosRequestConfig = {};
        if (parameters) {
            if (parameters.headers && parameters.headers.length) {
                config.headers = Object.fromEntries(parameters.headers.map(({ name, value }) => [`${name}`, value]));
            }
            if (parameters.cancelToken) {
                (config as any).cancelToken = parameters.cancelToken;
            }
            if (parameters.signal) {
                (config as any).signal = parameters.signal as any;
            }
        }
        return config;
    }

    static catchError(apiName: string, url: string, err: ApiError) {
        if (err && err.response) {
            if (err.response.status === 401 || isUserExpired()) {
                window.location.href = '/login';
            }
            console.log(`api ${apiName} ${url} error: ${err.response}`);
        }
    }

    static isApiError(err: any | unknown): err is ApiError {
        return err && err.response && typeof err.response.status === 'number';
    }
}

export default Api;
